import { useState, useCallback, useEffect } from 'react';

export interface HighlightedRow {
  id: number;
  timestamp: number;
}

/**
 * Custom hook for managing row highlighting in data grids
 * Automatically removes highlights after a specified duration
 */
export const useRowHighlighting = (duration: number = 3000) => {
  const [highlightedRows, setHighlightedRows] = useState<HighlightedRow[]>([]);

  /**
   * Add a row to the highlighted list
   */
  const highlightRow = useCallback((id: number) => {
    const timestamp = Date.now();
    setHighlightedRows((prev) => {
      const newRows = [
        ...prev.filter((row) => row.id !== id), // Remove existing highlight for this row
        { id, timestamp },
      ];
      return newRows;
    });
  }, []);

  /**
   * Remove a specific row from highlighting
   */
  const removeHighlight = useCallback((id: number) => {
    setHighlightedRows((prev) => prev.filter((row) => row.id !== id));
  }, []);

  /**
   * Clear all highlights
   */
  const clearAllHighlights = useCallback(() => {
    setHighlightedRows([]);
  }, []);

  /**
   * Check if a row is currently highlighted
   */
  const isRowHighlighted = useCallback(
    (id: number): boolean => {
      const isHighlighted = highlightedRows.some((row) => row.id === id);
      return isHighlighted;
    },
    [highlightedRows],
  );

  /**
   * Get the highlight timestamp for a row (useful for animations)
   */
  const getHighlightTimestamp = useCallback(
    (id: number): number | null => {
      const row = highlightedRows.find((row) => row.id === id);
      return row ? row.timestamp : null;
    },
    [highlightedRows],
  );

  // Auto-cleanup expired highlights using setTimeout instead of setInterval
  useEffect(() => {
    if (highlightedRows.length === 0) return;

    const timeout = setTimeout(() => {
      setHighlightedRows([]);
    }, duration);

    return () => clearTimeout(timeout);
  }, [highlightedRows, duration]);

  return {
    highlightedRows,
    highlightRow,
    removeHighlight,
    clearAllHighlights,
    isRowHighlighted,
    getHighlightTimestamp,
  };
};
